import React, { useState } from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import { GetServerSideProps } from 'next'
import Image from 'next/image'

import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import EarnGuide from '../src/components/Product/rewards/EarnGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'
import SavingsAccountsTable from '../src/components/Product/earn/savings-accounts/SavingsAccountsTable'

import { Cryptocurrency } from '../types/currency'
import { LoadState, Filters } from '../types/general'
import { SavingAccount } from '../types/savingAccount'

import FiltersProvider from '../src/contexts/Filters'
import { getCryptocurrencies } from '../src/services/currencies'
import { getSavingsAccounts } from '../src/services/savingAccounts'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
}))

const faqs = [
  {
    question: 'How do crypto savings accounts work?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
  {
    question: 'Can crypto savings accounts be hacked?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
  {
    question: 'How do crypto savings accounts make money?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
  {
    question: 'Can crypto savings accounts be tracked?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
  {
    question: 'Can I have multiple crypto savings account?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
  {
    question: 'Is a custodial wallet safer than a non-custodial wallet?',
    answer:
      'A crypto wallet is a piece of software that enables you to communicate with Blockchain networks in order to send and receive cryptocurrencies. At its core, a wallet consists of a public key, which is your personal identifier, and a private key, which allows you to sign transactions and authenticate yourself to the network.',
  },
]

interface SavingAccountsContainerProps {
  cryptocurrencies: Cryptocurrency[]
}

const SavingsAccounts = ({ cryptocurrencies }: SavingAccountsContainerProps): JSX.Element => {
  const classes = useStyles({})
  const [loadState, setLoadState] = useState<LoadState>(LoadState.IDLE)
  const [savingAccounts, setSavingAccounts] = useState<SavingAccount[]>([])
  const cefiAccounts = savingAccounts.filter((obj) => obj.type === 'CeFi')

  async function fetchSavingsAccounts(filters: Filters) {
    try {
      setLoadState(LoadState.LOADING)
      const data = await getSavingsAccounts('cefi', filters.depositCryptocurrency)
      setLoadState(LoadState.LOADED)
      setSavingAccounts(data)
    } catch (e) {
      setLoadState(LoadState.ERROR)
    }
  }
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <FiltersProvider>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`The Best Crypto Savings Accounts | Earn Interest | Cryptotesters`}
            description={`Crypto Savings Accounts let you earn interest on your Bitcoin, Ether or US stablecoins. We help you find the best, highest paying and most secure crypto savings account.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.savingAccounts}`,
              title: `The Best Crypto Savings Accounts | Earn Interest | Cryptotesters`,
              description: `Crypto Savings Accounts let you earn interest on your Bitcoin, Ether or US stablecoins. We help you find the best, highest paying and most secure crypto savings account.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">
                    The best <span className={classes.purpleText}> crypto savings accounts </span>{' '}
                  </Typography>
                  <Typography variant="body2">
                    Crypto Exchanges allow you to buy at the best market rate cryptocurrency using
                    conventional payment methods like by bank transfer or credit card.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href="#savings-accounts-table"
                    className={classes.heroCTA}
                  >
                    Compare savings accounts
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/earn/savings-account.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div id="savings-accounts-table">
              <SavingsAccountsTable
                cryptocurrencies={cryptocurrencies}
                onFilter={fetchSavingsAccounts}
                savingsAccounts={cefiAccounts}
                loadState={loadState}
                title="Compare crypto savings accounts"
              />
            </div>

            <div>
              <EarnGuide />
              <FAQSection faqs={faqs} />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </FiltersProvider>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let cryptocurrencies: Cryptocurrency[] = []

  try {
    cryptocurrencies = await getCryptocurrencies('finance')
  } catch (e) {
    console.error(e)
  }

  return {
    props: {
      cryptocurrencies,
    },
  }
}

export default SavingsAccounts
